import { validateAssetDescriptor, type AssetDescriptor } from '../assets/AssetContracts';

export const LOADING_SCREEN_SLOTS = ['startup', 'match'] as const;
export type LoadingScreenSlot = typeof LOADING_SCREEN_SLOTS[number];

export interface LoadingScreenFocalPoint {
  readonly x: number;
  readonly y: number;
}

export interface LoadingScreenSelection {
  readonly revisionId: string;
  readonly focalPoint: LoadingScreenFocalPoint;
}

export interface StoredLoadingScreenSettings {
  readonly startup: LoadingScreenSelection | null;
  readonly match: LoadingScreenSelection | null;
}

export interface LoadingScreenSettingsDto {
  readonly revision: number;
  readonly value: StoredLoadingScreenSettings;
}

export interface LoadingScreenRendition {
  readonly width: number;
  readonly height: number;
  readonly descriptor: AssetDescriptor;
}

export interface LoadingScreenBackground {
  readonly revisionId: string;
  readonly focalPoint: LoadingScreenFocalPoint;
  readonly renditions: readonly LoadingScreenRendition[];
}

export interface LoadingScreenSnapshot {
  readonly revision: number;
  readonly startup: LoadingScreenBackground | null;
  readonly match: LoadingScreenBackground | null;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function validateStoredLoadingScreenSettings(value: unknown): asserts value is StoredLoadingScreenSettings {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Loading screen settings must be an object.');
  const settings = value as Record<string, unknown>;
  if (Object.keys(settings).some(key => !LOADING_SCREEN_SLOTS.includes(key as LoadingScreenSlot))
    || !LOADING_SCREEN_SLOTS.every(slot => slot in settings)) {
    throw new Error('Loading screen settings must define startup and match slots.');
  }
  for (const slot of LOADING_SCREEN_SLOTS) validateSelection(settings[slot], slot);
}

export function loadingScreenRevisionIds(value: StoredLoadingScreenSettings): readonly string[] {
  return LOADING_SCREEN_SLOTS.flatMap(slot => value[slot] ? [value[slot].revisionId] : []);
}

export function validateLoadingScreenSnapshot(value: unknown): asserts value is LoadingScreenSnapshot {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Loading screen snapshot must be an object.');
  const snapshot = value as Record<string, unknown>;
  if (Object.keys(snapshot).some(key => key !== 'revision' && !LOADING_SCREEN_SLOTS.includes(key as LoadingScreenSlot))
    || !Number.isSafeInteger(snapshot.revision) || (snapshot.revision as number) < 1
    || !LOADING_SCREEN_SLOTS.every(slot => slot in snapshot)) {
    throw new Error('Loading screen snapshot is invalid.');
  }
  for (const slot of LOADING_SCREEN_SLOTS) validateBackground(snapshot[slot], slot);
}

function validateSelection(value: unknown, slot: LoadingScreenSlot): void {
  if (value === null) return;
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(`Loading screen ${slot} selection is invalid.`);
  const selection = value as Record<string, unknown>;
  if (Object.keys(selection).some(key => key !== 'revisionId' && key !== 'focalPoint')
    || typeof selection.revisionId !== 'string' || !UUID.test(selection.revisionId)
    || !selection.focalPoint || typeof selection.focalPoint !== 'object' || Array.isArray(selection.focalPoint)) {
    throw new Error(`Loading screen ${slot} selection is invalid.`);
  }
  const focalPoint = selection.focalPoint as Record<string, unknown>;
  if (Object.keys(focalPoint).some(key => key !== 'x' && key !== 'y')
    || !isUnitNumber(focalPoint.x) || !isUnitNumber(focalPoint.y)) {
    throw new Error(`Loading screen ${slot} focal point is invalid.`);
  }
}

function validateBackground(value: unknown, slot: LoadingScreenSlot): void {
  if (value === null) return;
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(`Loading screen ${slot} background is invalid.`);
  const background = value as Record<string, unknown>;
  if (Object.keys(background).some(key => key !== 'revisionId' && key !== 'focalPoint' && key !== 'renditions')
    || typeof background.revisionId !== 'string' || !UUID.test(background.revisionId)
    || !background.focalPoint || typeof background.focalPoint !== 'object' || Array.isArray(background.focalPoint)
    || !Array.isArray(background.renditions) || background.renditions.length === 0 || background.renditions.length > 8) {
    throw new Error(`Loading screen ${slot} background is invalid.`);
  }
  const focalPoint = background.focalPoint as Record<string, unknown>;
  if (Object.keys(focalPoint).some(key => key !== 'x' && key !== 'y')
    || !isUnitNumber(focalPoint.x) || !isUnitNumber(focalPoint.y)) {
    throw new Error(`Loading screen ${slot} focal point is invalid.`);
  }
  let previousWidth = 0;
  for (const candidate of background.renditions) {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
      throw new Error(`Loading screen ${slot} rendition is invalid.`);
    }
    const rendition = candidate as Record<string, unknown>;
    if (Object.keys(rendition).some(key => key !== 'width' && key !== 'height' && key !== 'descriptor')
      || !Number.isSafeInteger(rendition.width) || (rendition.width as number) <= previousWidth
      || !Number.isSafeInteger(rendition.height) || (rendition.height as number) <= 0) {
      throw new Error(`Loading screen ${slot} rendition is invalid.`);
    }
    validateAssetDescriptor(rendition.descriptor);
    if ((rendition.descriptor as AssetDescriptor).mimeType !== 'image/webp') {
      throw new Error(`Loading screen ${slot} rendition must use WebP.`);
    }
    previousWidth = rendition.width as number;
  }
}

function isUnitNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;
}
